/**
 * Ring (Donut) der Zeitverteilung (F-28). Jedes Segment hat einen Namen
 * (Tooltip, ADR 0003) - die Zahlen stehen ausserdem als Text in der Liste
 * daneben. In der Mitte die unverplante Zeit.
 */
import { useState } from 'react';

export interface RingSegment {
  key: string;
  name: string;
  farbe: string;
  minuten: number;
  text: string;
}

export function ZeitRing({ segmente, mitte, unter, groesse = 176, label, aktiv, onWahl }: {
  segmente: RingSegment[];
  mitte: string;
  unter?: string;
  groesse?: number;
  label: string;
  aktiv?: string | null;
  onWahl?: (key: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const breite = groesse * 0.13;
  const r = (groesse - breite) / 2;
  const umfang = 2 * Math.PI * r;
  const summe = segmente.reduce((s, x) => s + x.minuten, 0) || 1;
  const luecke = segmente.filter((s) => s.minuten > 0).length > 1 ? 2 : 0;
  const hervor = hover ?? aktiv ?? null;
  const gezeigt = segmente.find((s) => s.key === hover);

  // Beginn jedes Segments auf dem Umfang, vorab berechnet.
  const laengen = segmente.map((s) => (s.minuten / summe) * umfang);
  const anfaenge = laengen.map((_, i) => laengen.slice(0, i).reduce((a, b) => a + b, 0));
  return (
    <div className="relative shrink-0" style={{ width: groesse, height: groesse }}>
      <svg viewBox={`0 0 ${groesse} ${groesse}`} width={groesse} height={groesse} role="img" aria-label={label} className="-rotate-90">
        <circle cx={groesse / 2} cy={groesse / 2} r={r} fill="none" stroke="var(--color-line)" strokeWidth={breite} />
        {segmente.map((s, i) => {
          const strich = Math.max(0, laengen[i]! - luecke);
          return (
            <circle key={s.key} cx={groesse / 2} cy={groesse / 2} r={r} fill="none" stroke={s.farbe}
              strokeWidth={hervor === s.key ? breite + 4 : breite} strokeDasharray={`${strich} ${umfang - strich}`} strokeDashoffset={-anfaenge[i]!}
              opacity={hervor && hervor !== s.key ? 0.55 : 1}
              onPointerEnter={() => setHover(s.key)} onPointerLeave={() => setHover(null)}
              onClick={() => onWahl?.(s.key)} className={onWahl ? 'cursor-pointer' : undefined}>
              <title>{`${s.name}: ${s.text}`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
        {gezeigt ? (
          <div className="flex max-w-[70%] flex-col">
            <span className="truncate text-[12px] text-ink-muted">{gezeigt.name}</span>
            <span className="text-[15px] font-semibold tabular-nums">{gezeigt.text}</span>
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="text-[17px] font-semibold tabular-nums">{mitte}</span>
            {unter && <span className="text-[11px] text-ink-muted">{unter}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
